import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from 'src/components/common/Header';
import { MixingFormulationWithMaterials, WorkOrder } from '@twinsketch/topika-model';
import ApiService from 'src/services/ApiService';
import { useFetchDataById } from 'src/hook/CustomHook';
import { handleEdit } from 'src/hook/CommonHandler';
import { MaterialType } from '@twinsketch/topika-model';
import ROUTES from 'src/constants/routes';
import { useTranslation } from 'react-i18next';
import DetailHeader from 'src/components/detailPage/DetailHeader';
import ErrorModal from 'src/components/common/ErrorModal';
import BackButton from 'src/components/common/BackButton';
import { useUser } from 'src/auth/UserContext';
import { FiExternalLink } from 'react-icons/fi';
import FinalViscosityChart from '../workOrder/form/workOrderComponent/charts/FinalViscosityChart';
import NMPConsumptionChart from '../workOrder/form/workOrderComponent/charts/NMPConsumptionChart';
import SolidContentAccuracyChart from '../workOrder/form/workOrderComponent/charts/SolidContentAccuracyChart';
import 'src/styles/formulation-detail.css';
import 'src/styles/workOrder/work-order.css';
import InspectionTable from '../workOrder/form/workOrderComponent/tables/InspectionTable';

const MATERIAL_TYPES_ORDER = [
  MaterialType.ACTIVE_MATERIAL,
  MaterialType.CONDUCTING_AGENT,
  MaterialType.BINDER,
];

const FormulationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platformUser: user } = useUser();

  const [formulation, setFormulation] = useState<MixingFormulationWithMaterials | null>(null);
  const [associatedWorkOrders, setAssociatedWorkOrders] = useState<WorkOrder[]>([]);
  const [error, setError] = useState<string | null>(null);

  const canWrite = user?.permissions?.includes('write');

  // Fetch formulation details
  useFetchDataById(ApiService.fetchFormulationById, id, setFormulation, setError);

  // Fetch associated work orders
  useEffect(() => {
    if (!id) return;

    ApiService.fetchWorkOrdersByFormulationId(id)
      .then(setAssociatedWorkOrders)
      .catch(err => setError(err.message));
  }, [id]);

  // Computed values
  const allMixingWorks = useMemo(() => {
    return associatedWorkOrders.flatMap(workOrder =>
      (workOrder.mixingWorks || []).map(work => ({
        ...work,
        workOrderName: workOrder.name,
        workOrderId: workOrder.id,
      }))
    );
  }, [associatedWorkOrders]);

  const sortedMixingWorks = useMemo(() => {
    return [...allMixingWorks].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // Most recent first
    });
  }, [allMixingWorks]);

  const inspectionInfo = useMemo(() => {
    const workOrdersWithInspection = associatedWorkOrders
      .filter(wo => wo.inspectionBreakdown)
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt).getTime();
        return dateB - dateA; // Most recent first
      });

    return workOrdersWithInspection.length > 0
      ? workOrdersWithInspection[0].inspectionBreakdown
      : null;
  }, [associatedWorkOrders]);

  const hasAnalyticsData = inspectionInfo && sortedMixingWorks.length > 0;

  // Handlers
  const handleEditFormulation = () => {
    handleEdit(id!, navigate, setError, ROUTES.FORMULATIONS_EDIT(id!));
  };

  const handleInspectionUpdate = async () => {
    throw new Error('Cannot update inspection from formulation detail page');
  };

  if (!formulation) return <div>Loading...</div>;

  return (
    <div>
      <Header />
      <BackButton route={ROUTES.FORMULATIONS} />
      <ErrorModal message={error} onClose={() => setError(null)} />

      <div className="detail-page">
        <DetailHeader
          title={formulation.name}
          handleEdit={handleEditFormulation}
          handleDelete={null}
          createdAt={formulation.createdAt}
          updatedAt={formulation.updatedAt}
          createdBy={formulation.createdBy}
          updatedBy={formulation.updatedBy}
          canWrite={canWrite}
        />

        <div className="formulation-detail__body">
          <div className="formulation-detail__column">
            {/* Basic Information */}
            <BasicInformationSection formulation={formulation} t={t} />

            {/* Materials Composition */}
            <div
              className="section-divider"
              style={{ margin: '16px 0', borderTop: '2px solid #ccc' }}
            >
              <p style={{ fontSize: '12px', marginTop: '4px', color: '#888' }}>
                {t('Materials Composition')}
              </p>
            </div>
            <MaterialsCompositionSection formulation={formulation} />

            {/* Related Work Orders */}
            <RelatedWorkOrdersSection workOrders={associatedWorkOrders} t={t} />

            {/* Related Mixing Works */}
            <RelatedMixingWorksSection mixingWorks={sortedMixingWorks} t={t} />

            {/* Inspection Specifications */}
            {inspectionInfo && (
              <InspectionSpecificationsSection
                inspection={inspectionInfo}
                onUpdate={handleInspectionUpdate}
                electrodeType={formulation.type} // Pass the electrode type
                t={t}
              />
            )}

            {/* Analytics Charts */}
            {hasAnalyticsData && (
              <AnalyticsChartsSection
                mixingWorks={sortedMixingWorks}
                inspection={inspectionInfo}
                t={t}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Sub-components ==========

const BasicInformationSection: React.FC<{
  formulation: MixingFormulationWithMaterials;
  t: any;
}> = ({ formulation, t }) => (
  <div className="formulation-detail__section">
    <p>
      <strong>{t('formulationDataField.type')}:</strong>{' '}
      {t(`electrodeTypeEnum.${formulation.type}`)}
    </p>
    <p>
      <strong>{t('formulationDataField.locationTag')}:</strong>{' '}
      {formulation.locationTag || t('No location assigned')}
    </p>
  </div>
);

const MaterialsCompositionSection: React.FC<{
  formulation: MixingFormulationWithMaterials;
}> = ({ formulation }) => (
  <>
    {MATERIAL_TYPES_ORDER.map(type => {
      const elementsOfType = formulation.elements.filter(
        element => element.material.materialType === type
      );

      if (elementsOfType.length === 0) return null;

      return (
        <div key={type} className="formulation-detail__section">
          <ul className="formulation-detail__material-list">
            {elementsOfType.map(element => (
              <li key={element.materialId} className="formulation-detail__material-item">
                <div
                  className="formulation-detail__material-row"
                  style={{
                    display: 'flex',
                    width: '300px',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <Link
                    to={ROUTES.MATERIALS_DETAIL(element.materialId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Go to material detail"
                    className="formulation-detail__material-link"
                    style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
                  >
                    <span className="formulation-detail__material-name">
                      {element.material.materialName}
                    </span>
                    <span className="formulation-detail__material-model">
                      {element.material.modelName}
                    </span>
                    <FiExternalLink size={14} />
                  </Link>
                  <span
                    className="formulation-detail__material-ratio"
                    style={{ minWidth: '40px', textAlign: 'right' }}
                  >
                    {element.ratio}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    })}
  </>
);

const RelatedWorkOrdersSection: React.FC<{
  workOrders: WorkOrder[];
  t: any;
}> = ({ workOrders, t }) => (
  <div className="formulation-detail__related-works formulation-detail__section">
    <h3 className="formulation-detail__section-title">{t('Related Work Orders')}</h3>
    {workOrders.length > 0 ? (
      <ul className="formulation-detail__workorder-list">
        {workOrders.map(workOrder => (
          <li key={workOrder.id} className="formulation-detail__workorder-item">
            <Link
              to={ROUTES.WORK_ORDERS_DETAIL(workOrder.id)}
              target="_blank"
              rel="noopener noreferrer"
              title="Go to work order detail"
              className="formulation-detail__material-link"
            >
              <span className="formulation-detail__workorder-name">{workOrder.name}</span>
              <FiExternalLink size={14} style={{ marginLeft: '4px' }} />
            </Link>
          </li>
        ))}
      </ul>
    ) : (
      <p>{t('No related work orders found.')}</p>
    )}
  </div>
);

const RelatedMixingWorksSection: React.FC<{
  mixingWorks: any[];
  t: any;
}> = ({ mixingWorks, t }) => (
  <div className="formulation-detail__related-works formulation-detail__section">
    <h3 className="formulation-detail__section-title">{t('Related Mixing Works')}</h3>
    {mixingWorks.length > 0 ? (
      <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>{t('Date')}</th>
            <th>{t('Grind Size (μm)')}</th>
            <th>{t('Measured Viscosity (cP)')}</th>
            <th>{t('Measured Solid (%)')}</th>
            <th>{t('Work Order')}</th>
          </tr>
        </thead>
        <tbody>
          {mixingWorks.map((work, index) => (
            <tr key={index}>
              <td>{work.date ?? '—'}</td>
              <td>{work.grindSizeMicrons ?? '—'}</td>
              <td>{work.measuredViscosityCps ?? '—'}</td>
              <td>{work.measuredSolidContentPercent ?? '—'}</td>
              <td>
                <Link
                  to={ROUTES.WORK_ORDERS_DETAIL(work.workOrderId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Go to work order detail"
                  style={{ fontSize: '12px' }}
                >
                  {work.workOrderName}
                  <FiExternalLink style={{ marginLeft: '4px', fontSize: '12px' }} />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p>{t('No related mixing works found.')}</p>
    )}
  </div>
);

// Update the InspectionSpecificationsSection component signature
const InspectionSpecificationsSection: React.FC<{
  inspection: any;
  onUpdate: () => Promise<void>;
  electrodeType: any; // Add this prop
  t: any;
}> = ({ inspection, onUpdate, electrodeType, t }) => (
  <div className="formulation-detail__related-works formulation-detail__section">
    <h3 className="formulation-detail__section-title">{t('Inspection Specifications')}</h3>
    <InspectionTable
      inspection={inspection}
      onUpdate={onUpdate}
      readonly={true}
      electrodeType={electrodeType}
    />
  </div>
);

const AnalyticsChartsSection: React.FC<{
  mixingWorks: any[];
  inspection: any;
  t: any;
}> = ({ mixingWorks, inspection, t }) => (
  <div className="formulation-detail__related-works formulation-detail__section">
    <h3 className="formulation-detail__section-title">{t('Manufacturing Analytics')}</h3>
    <FinalViscosityChart mixingWorks={mixingWorks} inspectionBreakdown={inspection} />
    <SolidContentAccuracyChart mixingWorks={mixingWorks} inspectionBreakdown={inspection} />
    <NMPConsumptionChart mixingWorks={mixingWorks} />
  </div>
);

export default FormulationDetail;
